import React from 'react';
import { Tooltip } from '@material-ui/core';
import SchoolOutlinedIcon from '@material-ui/icons/SchoolOutlined';
import LanguageOutlinedIcon from '@material-ui/icons/LanguageOutlined';
import MailOutlineIcon from '@material-ui/icons/MailOutline';
import { formatProvinces } from '../../../utils/locationOptions';
import {
    EMPTY_VALUE,
    isBlank,
    orDash,
    buildDetailsText,
    renderWithDetails,
    renderTableDate,
} from '../../../Components/GenericTable/cellFormatters';

const mutedText = { color: 'var(--dm-muted, #647184)' };
const subtitleStyle = { ...mutedText, fontSize: '0.75rem', fontWeight: 400, whiteSpace: 'nowrap' };

const ONBOARDING_STATUS = {
    not_started: { label: 'Not started', color: '#475569', background: '#eef1f5' },
    in_progress: { label: 'In progress', color: '#9a6700', background: '#fff4d6' },
    completed: { label: 'Completed', color: '#1f7a3f', background: '#e3f4e8' },
};

const humanize = (value) => String(value).replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());

export function formatAdminName(adminDetails) {
    if (!adminDetails) return '';
    const fullName = [adminDetails.firstName, adminDetails.lastName].filter((part) => !isBlank(part)).join(' ');
    return fullName || adminDetails.userName || '';
}

export function buildCustomerDetails(row) {
    return buildDetailsText([
        ['Address', row.address],
        ['Sector', row.sector],
        ['Province', formatProvinces(row)],
        ['Branch', row.branch_name],
        ['Domain', row.domain],
    ]);
}

const renderAdmins = (row) => {
    const adminName = formatAdminName(row.admin_details);
    return (
        <div>
            <div>{Number(row.admin_count) || 0}</div>
            {adminName && <div style={subtitleStyle}>{adminName}</div>}
        </div>
    );
};

const renderOnboarding = (row) => {
    const status = row.onboarding?.overall_status;
    if (isBlank(status)) return EMPTY_VALUE;
    const config = ONBOARDING_STATUS[status] || { label: humanize(status), ...ONBOARDING_STATUS.not_started };
    const badge = (
        <span
            style={{
                display: 'inline-block',
                padding: '4px 10px',
                borderRadius: 6,
                fontSize: '0.75rem',
                fontWeight: 600,
                whiteSpace: 'nowrap',
                color: config.color,
                backgroundColor: config.background,
            }}
        >
            {config.label}
        </span>
    );
    const currentStep = row.onboarding?.current_step;
    if (isBlank(currentStep)) return badge;
    return (
        <Tooltip title={`Current step: ${humanize(currentStep)}`} placement="top" arrow>
            {badge}
        </Tooltip>
    );
};

const FLAGS = [
    { key: 'talent_access', label: 'Talent access', Icon: SchoolOutlinedIcon },
    { key: 'has_custom_domain', label: 'Custom domain', Icon: LanguageOutlinedIcon },
    { key: 'has_smtp', label: 'Custom SMTP', Icon: MailOutlineIcon },
];

const renderFlags = (row) => (
    <span style={{ display: 'inline-flex', gap: 6 }}>
        {FLAGS.map(({ key, label, Icon }) => {
            const enabled = Boolean(row[key]);
            return (
                <Tooltip key={key} title={`${label}: ${enabled ? 'Yes' : 'No'}`} placement="top" arrow>
                    <Icon
                        fontSize="small"
                        aria-label={`${label} ${enabled ? 'enabled' : 'disabled'}`}
                        style={{ color: enabled ? '#1f7a3f' : '#c3cad5' }}
                    />
                </Tooltip>
            );
        })}
    </span>
);

const renderCount = (key) => (row) => Number(row[key]) || 0;

export const customerColumns = [
    { label: 'Logo', key: 'logo' },
    { label: 'Name', key: 'name', render: (row) => renderWithDetails(row.name, buildCustomerDetails(row)) },
    { label: 'Partner', key: 'partner_label', render: (row) => orDash(row.partner_label) },
    { label: 'Created', key: 'created_at', render: (row) => renderTableDate(row.created_at) },
    { label: 'Country', key: 'country', render: (row) => orDash(row.country) },
    { label: 'Email', key: 'email', render: (row) => orDash(row.email) },
    { label: 'Contact', key: 'contact_no', render: (row) => orDash(row.contact_no) },
    { label: 'Licences', key: 'allocated_license', render: renderCount('allocated_license') },
    { label: 'Admins', key: 'admin_count', render: renderAdmins },
    { label: 'Projects', key: 'project_count', render: renderCount('project_count') },
    { label: 'Users', key: 'user_count', render: renderCount('user_count') },
    { label: 'Onboarding', key: 'onboarding', render: renderOnboarding },
    { label: 'Flags', key: 'flags', render: renderFlags },
    { label: 'Action', key: 'action', actions: ['edit'] },
];
